import classNames from "classnames";
import type Flickity from "flickity";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import playButtonCircle from "../../../img/misc/play-button-circle.png";
import { UGCListSubBlock } from "../../models/ugc";
import {
    detectTileVisibility,
    removeTabindex,
} from "../../utils/detectFlickityTileVisibility";
import { usePageSetting } from "../../utils/settings";
import { filterUGCTilesWithPosts } from "../../utils/ugcTiles";
import { Image } from "../Image";
import { SocialIcon } from "../SocialIcon";
import { UGCGallery } from "../UGCGallery";

// Not using useFlickity hook: requires mobile-state-dependent init and staticClick handling

const MOBILE_WIDTH_THRESHOLD = 501;
const LARGE_SCREEN_THRESHOLD = 960;

const isMobileWidth = (): boolean => {
    const window = getWindow();
    return (window?.innerWidth ?? 0) < MOBILE_WIDTH_THRESHOLD;
};

const checkDisplayTilesNumber = (): number => {
    const window = getWindow();
    return !((window?.innerWidth ?? 0) < LARGE_SCREEN_THRESHOLD) ? 3 : 2;
};

const isTilePhotoOrVideo = (tile: UGCListSubBlock): boolean => {
    return (
        tile.post !== null &&
        (tile.post.post_type === "image" || tile.post.post_type === "video") &&
        tile.post.photo !== null
    );
};

const isTextOnly = (tile: UGCListSubBlock): boolean => {
    return tile.post?.post_type === "text-only";
};

export const UGCCarousel = (
    props: Immutable<{
        tiles?: UGCListSubBlock[];
    }>,
): React.ReactNode => {
    const brandName = usePageSetting("app_display_name") || "";
    const tiles = React.useMemo(
        () => filterUGCTilesWithPosts(props.tiles),
        [props.tiles],
    );
    const ugcCarouselRef = React.useRef<HTMLDivElement | null>(null);
    const carouselFlktyRef = React.useRef<Flickity | null>(null);
    const flickityClassRef = React.useRef<typeof Flickity | null>(null);
    const didUpdateRef = React.useRef(false);

    const [isMobile, setIsMobile] = React.useState(false);
    const [isMobileReady, setIsMobileReady] = React.useState(false);
    const [showGallery, setShowGallery] = React.useState(false);
    const [selectedSlide, setSelectedSlide] = React.useState(0);
    const [selectedTilesGroupRange, setSelectedTilesGroupRange] =
        React.useState(0);
    const [selectedTileIndex, setSelectedTileIndex] = React.useState(0);

    const onGalleryClose = () => {
        setShowGallery(false);
    };

    const onGalleryOpen = (showGalleryValue: boolean, cellIndex: number) => {
        setShowGallery(showGalleryValue);
        setSelectedSlide(cellIndex);
    };

    const openSelectedSlide = (
        cellIndex: number,
        event: React.KeyboardEvent<HTMLButtonElement>,
    ) => {
        if (event) {
            event.preventDefault();
        }
        onGalleryOpen(true, cellIndex);
    };

    const initializeBaseCarousel = () => {
        if (carouselFlktyRef.current || !flickityClassRef.current) {
            return;
        }

        const FlickityClass = flickityClassRef.current;
        const cellAlignment = isMobile ? "center" : "left";
        const initialIndex = isMobile ? 1 : 0;

        if (ugcCarouselRef.current) {
            carouselFlktyRef.current = new FlickityClass(
                ugcCarouselRef.current,
                {
                    // Ignore SR instruction <p>; only real tiles are cells (A360 #390).
                    cellSelector: ".ugc-carousel-tile-container",
                    cellAlign: cellAlignment,
                    contain: true,
                    groupCells: true,
                    pageDots: false,
                    imagesLoaded: true,
                    initialIndex: initialIndex,
                    arrowShape: {
                        x0: 25,
                        x1: 60,
                        y1: 50,
                        x2: 65,
                        y2: 50,
                        x3: 30,
                    },
                },
            );

            setSelectedTilesGroupRange(
                Number(carouselFlktyRef.current.selectedIndex) +
                    Number(checkDisplayTilesNumber() - 1),
            );
            setSelectedTileIndex(carouselFlktyRef.current.selectedIndex);

            carouselFlktyRef.current.on("select", () => {
                if (carouselFlktyRef.current) {
                    setSelectedTilesGroupRange(
                        Number(carouselFlktyRef.current.selectedIndex) +
                            Number(checkDisplayTilesNumber() - 1),
                    );
                    setSelectedTileIndex(
                        carouselFlktyRef.current.selectedIndex,
                    );
                }
            });

            carouselFlktyRef.current.on(
                "staticClick",
                (_, __, ___, cellIndex) => {
                    onGalleryOpen(true, cellIndex);
                },
            );
        }
    };

    // Required: Event listener needs stable reference for proper cleanup
    const checkMobileState = React.useCallback(() => {
        const mobile = isMobileWidth();

        setIsMobile(mobile);
        setIsMobileReady(true);
    }, []);

    React.useEffect(() => {
        const window = getWindow();
        if (!window) {
            return;
        }
        checkMobileState();
        window.addEventListener("resize", checkMobileState);

        return () => {
            window.removeEventListener("resize", checkMobileState);
        };
    }, [checkMobileState]);

    React.useEffect(() => {
        if (!tiles.length || !isMobileReady) {
            return;
        }

        // Skip on SSR - flickity requires window
        if (import.meta.env.SSR) {
            return;
        }

        if (flickityClassRef.current) {
            initializeBaseCarousel();
        } else {
            import("flickity").then((module) => {
                flickityClassRef.current = module.default;
                initializeBaseCarousel();
            });
        }
    }, [tiles, isMobile, isMobileReady]);

    React.useEffect(() => {
        if (!didUpdateRef.current) {
            didUpdateRef.current = true;
            return;
        }

        // ADA - Remove tabindex attribute which is added by flickity to avoid problems
        // for screen reader and keyboard users by disrupting the natural tab order
        removeTabindex("ugc-carousel__container");
    });

    const buildCarouselTiles = () => {
        const tilesNumber = tiles.length;
        let isTileInViewport = true;

        return tiles.map((tile, cellIndex) => {
            if (carouselFlktyRef.current) {
                isTileInViewport = detectTileVisibility(
                    tilesNumber,
                    cellIndex,
                    selectedTilesGroupRange,
                    selectedTileIndex,
                );
            }
            // Off-viewport tiles on non-mobile are unreachable via inert.
            const isTileHidden = !isMobile && !isTileInViewport;
            const tileBaseClasses = classNames({
                "ugc-carousel-tile": true,
                "ugc-carousel-tile--text":
                    isTextOnly(tile) && !tile.post.avatar,
                "ugc-carousel-tile--avatar":
                    isTextOnly(tile) && tile.post.avatar,
                "ugc-carousel-tile--photo": tile.post.post_type === "image",
                "ugc-carousel-tile--video": tile.post.post_type === "video",
            });
            const tileAttributionMobile = classNames({
                "ugc-carousel-tile__attribution": true,
                "ugc-carousel-tile__attribution--text-only": isTextOnly(tile),
            });
            const tilePhotoClasses = classNames({
                "ugc-carousel-tile__photo": true,
                "ugc-carousel-tile__photo--video":
                    tile.post.post_type === "video",
            });
            const tileIconContainerClasses = classNames({
                "ugc-carousel-tile__icon-container": true,
                "ugc-carousel-tile__icon-container--empty":
                    tile.post.social_media === "none",
            });

            const playButtonImage = playButtonCircle;
            // Inline background-image rather than a deferred loader: flickity
            // clones and reorders these tile nodes, so a per-tile
            // IntersectionObserver bound to a React ref would miss the clones.
            // The rendered value matches SSR output, so hydration is stable.
            const tilePhoto = isTilePhotoOrVideo(tile) ? (
                <div
                    className={tilePhotoClasses}
                    style={{
                        backgroundImage: `url(${tile.post.photo})`,
                    }}
                >
                    {tile.post.post_type === "video" && (
                        <Image
                            src={playButtonImage}
                            className="ugc-carousel-tile__video-icon"
                            alt="play video"
                        />
                    )}
                </div>
            ) : null;
            const mobileInfo = isMobile ? (
                <div className="ugc-carousel-tile__info">
                    <div className="ugc-carousel-tile__info--inner-mobile">
                        {!isTextOnly(tile) && (
                            <div className="ugc-carousel-tile__copy">
                                {tile.post.pull_quote}
                                <span className="ugc-carousel-tile__copy--blue">
                                    {t`View More`}
                                </span>
                            </div>
                        )}
                        <div className={tileAttributionMobile}>
                            <span className={tileIconContainerClasses}>
                                <SocialIcon
                                    type={tile.post.social_media}
                                    className={"ugc-carousel-tile__icon"}
                                />
                            </span>
                            <span className="ugc-carousel-tile__username">
                                {tile.post.username}
                            </span>
                        </div>
                    </div>
                </div>
            ) : null;
            const mobileTextOnly =
                isMobile && isTextOnly(tile) ? (
                    <div className="ugc-carousel-tile__copy--mobile-text-only">
                        {tile.post.copy}
                    </div>
                ) : null;
            const nonMobileInfo = !isMobile ? (
                <div className="ugc-carousel-tile__info">
                    <div className="ugc-carousel-tile__copy">
                        {tile.post.pull_quote}
                        {tile.post.view_post && (
                            <span className="ugc-carousel-tile__copy--blue al-ugc-carousel__view-more">
                                {t`View More`}
                            </span>
                        )}
                    </div>
                </div>
            ) : null;
            const nonMobileAttribution = !isMobile ? (
                <div className="ugc-carousel-tile__attribution">
                    <span className={tileIconContainerClasses}>
                        <SocialIcon
                            type={tile.post.social_media}
                            className={"ugc-carousel-tile__icon"}
                        />
                    </span>
                    <span className="ugc-carousel-tile__username">
                        {tile.post.username}
                    </span>
                </div>
            ) : null;
            return (
                <button
                    inert={isTileHidden}
                    className="ugc-carousel-tile-container"
                    onKeyPress={openSelectedSlide.bind(null, cellIndex)}
                    key={cellIndex}
                >
                    <div className={tileBaseClasses}>
                        {mobileInfo}
                        {tilePhoto}
                        {mobileTextOnly}
                        {nonMobileInfo}
                        {nonMobileAttribution}
                    </div>
                </button>
            );
        });
    };

    return (
        <span>
            <div
                role="region"
                aria-label={
                    brandName
                        ? t`${brandName} social posts carousel`
                        : t`Social posts carousel`
                }
                className="ugc-carousel__container"
                ref={ugcCarouselRef}
            >
                {tiles.length ? (
                    <p className="ada-screenreader-only">
                        {t`This is a carousel of social posts. Use the previous and next buttons to move between posts, and Enter or Space to open a post.`}
                    </p>
                ) : null}
                {buildCarouselTiles()}
            </div>
            {showGallery && tiles.length > 0 && (
                <UGCGallery
                    tiles={tiles}
                    initialSlide={selectedSlide}
                    onCloseGallery={onGalleryClose}
                />
            )}
        </span>
    );
};
